import React, { useEffect, useState } from 'react';
import { Mail, RotateCcw, Loader2, Check, Calendar, ChevronRight } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import axios from 'axios';
import { toast } from 'sonner';
import { formatDateTimeET } from '../../utils/formatters';

const money = (v) => (v === null || v === undefined || v === '' ? '—' : `$${Number(v).toFixed(2)}`);

export const MAILAWAY_ACTIVE_STATUSES = new Set(['pending_send', 'sent', 'confirmed', 'awaiting_response']);
export const activeMailAwayRequests = (order) => (order?.mail_away_requests || [])
  .filter((r) => MAILAWAY_ACTIVE_STATUSES.has(String(r.status || '').toLowerCase()));


// MAILAWAY-RECOVERY-001 / MAILAWAY-PREMIUM-UI-003: compact ice-blue banner — Order Summary only.
// Shows the dynamic department/jurisdiction names of EVERY active request and one Return action
// (the dialog lets the user pick when several are active). Business rules/audit live in the backend.
export const MailAwayRecoveryBanner = ({ order, onReturn, compact = false }) => {
  const [opening, setOpening] = useState(false);
  const active = activeMailAwayRequests(order);
  const status = String(order?.status || '');
  if (!active.length || ['mail_away', 'complete', 'completed', 'cancelled'].includes(status)) return null;
  const names = active.map((r) => r.jurisdiction_name || r.department_name).filter(Boolean);
  const label = names.length ? names.join(' · ') : 'Active request';
  const handle = async () => {
    if (opening) return; // duplicate-click guard
    setOpening(true);
    try { await onReturn(active); } finally { setTimeout(() => setOpening(false), 400); }
  };
  return (
    <div
      className={`ma-banner-in rounded-xl border border-blue-200/80 bg-gradient-to-r from-sky-50 via-white to-blue-50/60 shadow-[0_6px_18px_-12px_rgba(37,99,235,0.35)] ${compact ? 'px-3 py-2' : 'px-4 py-2.5'} flex flex-col sm:flex-row sm:items-center gap-2.5`}
      data-testid="mailaway-recovery-banner"
      data-order-status={status}
      data-request-count={active.length}
    >
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <div className="w-8 h-8 rounded-lg bg-blue-600 text-white flex items-center justify-center shrink-0 shadow-[0_4px_10px_-4px_rgba(37,99,235,0.6)]"><Mail className="h-4 w-4" /></div>
        <div className="min-w-0">
          <div className="text-[13px] font-semibold text-slate-800 leading-tight" data-testid="mailaway-recovery-title">
            Active Mail-Away request{active.length === 1 ? '' : 's'} already exist{active.length === 1 ? 's' : ''}
          </div>
          <div className="text-xs text-blue-800/90 mt-0.5 truncate" data-testid="mailaway-recovery-detail" title={label}>
            {names.map((n, i) => <span key={i} data-testid="mailaway-recovery-request">{i > 0 && <span className="text-slate-300 mx-1">·</span>}{n}</span>)}
            {!names.length && label}
          </div>
        </div>
      </div>
      <Button type="button" size="sm" onClick={handle} disabled={opening} data-testid="mailaway-recovery-return-btn"
        className="ma-return-btn group h-8 px-3.5 rounded-lg text-white gap-1.5 shrink-0 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-600 hover:to-indigo-500 shadow-[0_4px_12px_-4px_rgba(37,99,235,0.55)] focus-visible:ring-2 focus-visible:ring-blue-500/50 focus-visible:ring-offset-1 disabled:opacity-80">
        {opening ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5 ma-return-icon" />}
        {opening ? 'Opening…' : <>Return to Mail-Away <span aria-hidden className="ma-return-arrow">→</span></>}
      </Button>
    </div>
  );
};

export const PendingOverrideDialog = ({ state, onClose, onConfirm }) => (
  <Dialog open={state.open} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="max-w-lg" data-testid="pending-override-dialog">
      <DialogHeader>
        <DialogTitle className="text-slate-900">Move to Pending anyway?</DialogTitle>
        <DialogDescription>
          This order still has an active Mail-Away request ({state.active.map((r) => r.jurisdiction_name).filter(Boolean).join(', ') || 'see Order Summary'}).
          The request stays active and unchanged; this override is recorded in the Activity log with the request ID(s). Processors cannot do this — prefer adding a Mail-Away follow-up.
        </DialogDescription>
      </DialogHeader>
      <DialogFooter>
        <Button type="button" variant="outline" data-testid="pending-override-cancel" onClick={onClose}>Cancel</Button>
        <Button type="button" data-testid="pending-override-confirm" onClick={onConfirm} className="bg-amber-600 hover:bg-amber-700 text-white">Move to Pending (override)</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
const fmtSent = (r) => {
  const v = r.sent_at || r.date_sent || r.created_at;
  if (!v) return null;
  try { return formatDateTimeET(v).split(' ')[0]; } catch { return String(v).slice(0, 10); }
};

// MAILAWAY-RETURN-001 / MAILAWAY-RETURN-MODAL-PREMIUM-004: compact premium reason-only dialog.
// Business rules unchanged — the existing request is resumed via the same backend call (no new request,
// fee, shipping, letter or email). `succeeded` shows a brief confirmation before the parent closes.
export const ReturnToMailAwayDialog = ({ open, onOpenChange, requests, initialReason = '', isSubmitting, succeeded = false, onConfirm }) => {
  const [reason, setReason] = useState(initialReason);
  const [touched, setTouched] = useState(false);
  const [attempted, setAttempted] = useState(false);
  useEffect(() => { if (open) { setReason(initialReason || ''); setTouched(false); setAttempted(false); } }, [open, initialReason]);
  const tooShort = reason.trim().length < 10;
  const showErr = tooShort && (touched || attempted);
  const canSubmit = !tooShort && !isSubmitting && !succeeded;
  const submit = () => { if (isSubmitting || succeeded) return; if (tooShort) { setAttempted(true); return; } onConfirm(reason.trim()); };

  return (
    <Dialog open={open} onOpenChange={(o) => !isSubmitting && onOpenChange(o)}>
      <DialogContent className="sm:max-w-[560px] p-0 gap-0 overflow-hidden rounded-2xl border border-slate-200/80 shadow-[0_24px_60px_-20px_rgba(15,23,42,0.35)]" data-testid="return-mailaway-dialog">
        <DialogHeader className="px-6 pt-5 pb-4 flex-row items-center gap-3 space-y-0 text-left">
          <span className="h-11 w-11 rounded-xl bg-gradient-to-br from-sky-50 to-blue-100 border border-blue-100 text-blue-600 inline-flex items-center justify-center shrink-0 shadow-[0_6px_16px_-10px_rgba(37,99,235,0.6)]">
            <RotateCcw className="h-5 w-5 ma-return-icon-anim" />
          </span>
          <div className="min-w-0">
            <DialogTitle className="text-lg font-semibold tracking-tight text-slate-900">Return to Mail-Away</DialogTitle>
            <DialogDescription className="text-sm text-slate-500 mt-0.5">Resume the existing Mail-Away request.</DialogDescription>
          </div>
        </DialogHeader>

        <div className="px-6 space-y-3">
          <div className="space-y-2" data-testid="return-mailaway-existing-requests">
            {requests.map((r, i) => (
              <div key={r.request_id} className="ma-return-row-in relative overflow-hidden flex items-center gap-3 rounded-xl border border-blue-100 bg-gradient-to-r from-sky-50/80 via-white to-blue-50/50 px-3.5 py-3" style={{ animationDelay: `${Math.min(i, 5) * 40}ms` }}>
                <span className="h-10 w-10 rounded-lg bg-white border border-blue-100 text-blue-600 inline-flex items-center justify-center shrink-0 shadow-sm"><Mail className="h-4.5 w-4.5" /></span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
                    <span className="text-[15px] font-semibold text-slate-900 truncate" data-testid="return-mailaway-request-name">{r.jurisdiction_name || r.department_name || 'Jurisdiction'}</span>
                    <span className="rounded-full bg-sky-100 text-sky-700 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide" data-testid="return-mailaway-request-status">{String(r.status || '').replace(/_/g, ' ')}</span>
                  </div>
                  <div className="mt-0.5 text-xs text-slate-500 flex flex-wrap items-center gap-x-2">
                    {fmtSent(r) && <span className="inline-flex items-center gap-1" data-testid="return-mailaway-request-sent"><Calendar className="h-3.5 w-3.5 text-slate-400" />Sent · {fmtSent(r)}</span>}
                    {r.tax_office_fee !== null && r.tax_office_fee !== undefined && r.tax_office_fee !== '' && <span>· MLC {money(r.tax_office_fee)}</span>}
                    {r.shipping_fee !== null && r.shipping_fee !== undefined && r.shipping_fee !== '' && <span>· Shipping {money(r.shipping_fee)}</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div>
            <label htmlFor="return-mailaway-reason" className="block text-[13px] font-semibold text-slate-700 mb-1.5">
              Reason for return <span className="text-rose-500">*</span>
            </label>
            <Textarea
              id="return-mailaway-reason"
              data-testid="return-mailaway-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value.slice(0, 2000))}
              onBlur={() => setTouched(true)}
              placeholder="Enter reason…"
              rows={2}
              aria-invalid={showErr || undefined}
              className={`resize-none rounded-xl border-slate-200 shadow-sm text-sm transition-[border-color,box-shadow] duration-200 hover:border-slate-300 focus-visible:ring-2 focus-visible:ring-blue-500/40 focus-visible:border-blue-400 ${showErr ? 'border-rose-300 focus-visible:ring-rose-300/40' : ''}`}
            />
            <div className="mt-1 flex items-center justify-between text-[11px]">
              <span className={`font-medium text-rose-600 mls-ma-err-in ${showErr ? '' : 'invisible'}`} data-testid="return-mailaway-reason-error">{showErr ? 'Reason for return is required (min 10 characters).' : '·'}</span>
              <span className="text-slate-400">{reason.length}/2000</span>
            </div>
          </div>
        </div>

        <DialogFooter className="px-6 py-4 mt-1 border-t border-slate-100 flex-row justify-end gap-2.5 sm:space-x-0">
          <Button type="button" variant="outline" data-testid="return-mailaway-cancel" onClick={() => onOpenChange(false)} disabled={isSubmitting}
            className="h-11 px-6 rounded-xl border-slate-200 text-slate-700 hover:bg-slate-50 focus-visible:ring-2 focus-visible:ring-blue-500/40 transition-colors">Cancel</Button>
          <Button type="button" data-testid="return-mailaway-confirm" onClick={submit} disabled={!canSubmit && !tooShort} aria-disabled={!canSubmit || undefined}
            className={`ma-return-btn group h-11 px-5 rounded-xl text-[15px] font-semibold text-white gap-2 focus-visible:ring-2 focus-visible:ring-blue-500/50 focus-visible:ring-offset-2 ${
              succeeded ? 'bg-emerald-600 hover:bg-emerald-600' : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:to-indigo-500'} ${tooShort && !isSubmitting && !succeeded ? 'opacity-60 shadow-none' : 'shadow-[0_6px_16px_-6px_rgba(37,99,235,0.6)]'}`}>
            {succeeded ? <><Check className="h-4 w-4" strokeWidth={3} /> Returned to Mail-Away</>
              : isSubmitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Returning…</>
                : <><RotateCcw className="h-4 w-4 ma-return-icon" /> Return to Mail-Away <ChevronRight className="h-4 w-4 ma-return-arrow" /></>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const RECOVERY_ROLES = new Set(['admin', 'tax_search_team_leader', 'title_search_team_leader', 'processor', 'title_search_processor']);
export const MailAwayRecoveryCard = ({ order, currentUser, compact = false, onReturned, className = 'mt-3' }) => {
  const [state, setState] = useState({ open: false, requests: [] });
  const [submitting, setSubmitting] = useState(false);
  const [succeeded, setSucceeded] = useState(false);
  if (!RECOVERY_ROLES.has(currentUser?.role) || !activeMailAwayRequests(order).length) return null;

  const handleConfirm = async (reasonText) => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const resp = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/orders/${order.id}/mailaway/return`,
        { reason: reasonText, source: 'summary' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSucceeded(true); // brief confirmation state on the button, then close
      toast.success('Returned to Mail-Away', { description: resp?.data?.message || 'Existing request kept — no new charge.', duration: 3500 });
      setTimeout(() => { setState({ open: false, requests: [] }); setSucceeded(false); if (onReturned) onReturned(resp?.data); }, 900);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Failed to return to Mail-Away'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={className} data-testid="mailaway-recovery-card">
      <MailAwayRecoveryBanner order={order} compact={compact} onReturn={(active) => setState({ open: true, requests: active })} />
      <ReturnToMailAwayDialog
        open={state.open}
        onOpenChange={(o) => setState((s) => ({ ...s, open: o }))}
        requests={state.requests}
        isSubmitting={submitting}
        succeeded={succeeded}
        onConfirm={handleConfirm}
      />
    </div>
  );
};
